import type {
  BuyerInterviewQuestionDto,
  BuyerInterviewRegistryDto,
} from "@/api/buyers";
import {
  describeAnswer,
  isAsked,
  isRecorded,
  questionsForSection,
  type DraftAnswers,
} from "./interviewBits";

/**
 * Every recorded answer, grouped by section — the review step of a live
 * interview and the whole body of a read-only prior version.
 *
 * <p>🔴 <b>It renders answers and nothing else.</b> No tone, no colour, no
 * "strong"/"weak", no knockout marker: a transcript that shaded its own rows
 * would be a rating hint reaching the specialist mid-call, which is precisely
 * what the interview exists to avoid. Verbatim answers are marked, because
 * "these are their words" is a fact about how the answer was recorded rather
 * than a judgement of it.</p>
 *
 * <p>Unrecorded questions are listed too, muted, so a gap is visible as a gap.
 * A branch that never opened is not listed at all — it was never asked — and
 * neither is a retired question with nothing recorded (`isAsked`).</p>
 */
export function InterviewTranscript({
  registry,
  answers,
  emptyHint = "Nothing recorded yet.",
}: {
  registry: BuyerInterviewRegistryDto | undefined;
  answers: DraftAnswers;
  emptyHint?: string;
}) {
  if (!registry) return null;

  return (
    <div className="grid gap-space-4" data-testid="interview-transcript">
      {registry.sections.map((section) => {
        const visible = questionsForSection(registry, section.id).filter((q) =>
          isAsked(q, answers),
        );
        if (visible.length === 0) return null;

        return (
          <section key={section.id}>
            <h3 className="font-display font-bold text-small text-onyx m-0 mb-space-2">
              {section.title}
            </h3>
            <dl className="grid gap-space-2 m-0">
              {visible.map((question) => (
                <TranscriptRow
                  key={question.id}
                  question={question}
                  value={describeAnswer(question, answers[question.id])}
                  recorded={isRecorded(answers[question.id])}
                  emptyHint={emptyHint}
                />
              ))}
            </dl>
          </section>
        );
      })}
    </div>
  );
}

function TranscriptRow({
  question,
  value,
  recorded,
  emptyHint,
}: {
  question: BuyerInterviewQuestionDto;
  value: string;
  recorded: boolean;
  emptyHint: string;
}) {
  return (
    <div className="border-b border-border-light pb-space-2 last:border-b-0">
      <dt className="text-caption text-gray-mid">
        {question.label}
        {question.verbatim && (
          <span className="ml-space-2 uppercase tracking-wide">
            · in their words
          </span>
        )}
      </dt>
      <dd
        className={
          recorded
            ? "text-small text-onyx m-0 mt-space-1 whitespace-pre-wrap"
            : "text-small text-gray-mid m-0 mt-space-1 italic"
        }
      >
        {recorded ? value : emptyHint}
      </dd>
    </div>
  );
}
